import { canManageInviteForRole } from '../auth/role-authority';
import type { Role } from '../auth/types';
import type { Personnel, PersonnelCategory, PrimaryRole } from '../personnel/types';

export interface QueuedInviteDraft {
    personnelId: string;
    email: string;
    name: string;
    productionTitle: string;
    roleId: string;
    rosterPersonId?: string;
    inviteMessage?: string;
}

export type InviteDraftInvalidReason = 'email-required' | 'access-role-required';

export const PRIMARY_ROLE_LABELS: Record<PrimaryRole, string> = {
    producer: 'Producer',
    'production-manager': 'Production Manager',
    director: 'Director',
    'assistant-director': 'Assistant Director',
    'stage-manager': 'Stage Manager',
    'assistant-stage-manager': 'Assistant Stage Manager',
    'music-director': 'Music Director',
    choreographer: 'Choreographer',
    'scenic-designer': 'Scenic Designer',
    'costume-designer': 'Costume Designer',
    'lighting-designer': 'Lighting Designer',
    'sound-designer': 'Sound Designer',
    'props-designer': 'Props Designer',
    'master-electrician': 'Master Electrician',
    'master-carpenter': 'Master Carpenter',
    'wardrobe-supervisor': 'Wardrobe Supervisor',
    'crew-general': 'Crew',
    actor: 'Actor',
    swing: 'Swing',
    understudy: 'Understudy',
    standby: 'Standby',
    other: 'Team Member',
};

const CATEGORY_LABELS: Record<PersonnelCategory, string> = {
    cast: 'Cast',
    production: 'Production Team',
    crew: 'Crew',
};

export function getProductionTitle(person: Personnel): string {
    const customTitle = person.title?.trim();
    if (customTitle) return customTitle;

    if (person.primaryRole) {
        return PRIMARY_ROLE_LABELS[person.primaryRole];
    }

    return CATEGORY_LABELS[person.category];
}

export function getAssignableInviteRoles(input: {
    roles: Role[];
    actorRole: Role | null | undefined;
    isProjectOwner?: boolean;
    isSuperAdmin?: boolean;
}): Role[] {
    return input.roles.filter((role) => (
        role.id !== 'pending'
        && canManageInviteForRole({
            actorRole: input.actorRole,
            targetRole: role,
            isProjectOwner: input.isProjectOwner,
            isSuperAdmin: input.isSuperAdmin,
        })
    ));
}

export function validateQueuedInviteDraft(
    draft: QueuedInviteDraft
): { valid: true } | { valid: false; reason: InviteDraftInvalidReason } {
    if (!draft.email.trim()) {
        return { valid: false, reason: 'email-required' };
    }

    if (!draft.roleId.trim()) {
        return { valid: false, reason: 'access-role-required' };
    }

    return { valid: true };
}

export function buildInviteRecipientFromDraft(draft: QueuedInviteDraft): {
    personnelId: string;
    email: string;
    name: string;
    roleId: string;
    productionTitle: string;
    rosterPersonId?: string;
    inviteMessage?: string;
} {
    const productionTitle = draft.productionTitle.trim() || 'Team Member';
    const inviteMessage = draft.inviteMessage?.trim();

    return {
        personnelId: draft.personnelId,
        email: draft.email.trim(),
        name: draft.name.trim(),
        roleId: draft.roleId,
        productionTitle,
        ...(draft.rosterPersonId ? { rosterPersonId: draft.rosterPersonId } : {}),
        ...(inviteMessage ? { inviteMessage } : {}),
    };
}
